// Offline / sync indicator pill. Lives in the topbar; hidden when
// we're online with an empty queue (the normal-case state). Surfaces:
//   • online + draining  → "Syncing 3…"
//   • online + idle + queue empty → hidden
//   • offline + queue empty → "Offline"
//   • offline + queue > 0   → "Offline · 3 queued"
//
// Pure presentational — the data + transitions live in useOfflineStatus.

import { useOfflineStatus } from "../lib/use-offline-status";

export function OfflineIndicator() {
  const { online, queueDepth, draining } = useOfflineStatus();

  // The happy path: hide entirely. Nothing to surface to the user.
  if (online && queueDepth === 0 && !draining) return null;

  const state = draining ? "draining"
    : online ? "syncing"          // online with queue but drain not started yet
    : "offline";

  const label = draining
    ? (queueDepth > 0 ? `Syncing ${queueDepth}…` : "Syncing…")
    : online
      ? (queueDepth > 0 ? `${queueDepth} queued` : "Online")
      : queueDepth > 0
        ? `Offline · ${queueDepth} queued`
        : "Offline";

  return (
    <span className="offline-pill" data-state={state} title={tooltipFor(state, queueDepth)}>
      <span className="offline-pill-dot" />
      {label}
    </span>
  );
}

function tooltipFor(state: string, depth: number): string {
  if (state === "offline") {
    return depth > 0
      ? `You're offline. ${depth} change(s) will sync when the network comes back.`
      : "You're offline. Reads are coming from cache.";
  }
  if (state === "draining") return "Replaying queued changes…";
  if (state === "syncing")  return `${depth} change(s) queued — drain runs on reconnect.`;
  return "";
}
